import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";
import { useAuth } from "../context/AuthContext";
import { loadActivityLookups } from "../utils/activityLookups";

function formatCurrency(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
}

function formatDate(value) {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleDateString();
}

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleString();
}

function formatUserName(item) {
  return [item?.firstname, item?.lastname].filter(Boolean).join(" ") || item?.username || "Unknown user";
}

function getActivityStatusBadgeClass(status) {
  switch (status) {
    case "Completed":
      return "status-badge status-badge-completed";
    case "In Progress":
      return "status-badge status-badge-progress";
    case "On Hold":
      return "status-badge status-badge-hold";
    case "Not Done":
    case "Cancelled":
      return "status-badge status-badge-not-done";
    case "Pending":
    default:
      return "status-badge status-badge-pending";
  }
}

function numberOrNull(value) {
  return value === "" ? null : Number(value);
}

function formatNumberInput(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const normalized = String(value).replace(/,/g, "");
  if (normalized === "" || Number.isNaN(Number(normalized))) {
    return value;
  }

  return Number(normalized).toLocaleString(undefined, {
    maximumFractionDigits: normalized.includes(".") ? 2 : 0
  });
}

function normalizeNumberInput(value) {
  return value.replace(/,/g, "");
}

function parseNumber(value) {
  if (value === "" || value === null || value === undefined) {
    return 0;
  }

  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function downloadBlob(blob, fileName) {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName || "report";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
}

function inferPreviewKind(fileName, fileType) {
  const normalizedName = String(fileName || "").toLowerCase();
  const normalizedType = String(fileType || "").toLowerCase();

  if (normalizedType.startsWith("image/")) {
    return "image";
  }
  if (normalizedType.startsWith("video/")) {
    return "video";
  }
  if (normalizedType.startsWith("audio/")) {
    return "audio";
  }
  if (normalizedType.includes("pdf") || normalizedName.endsWith(".pdf")) {
    return "pdf";
  }
  if (
    normalizedType.startsWith("text/") ||
    normalizedName.endsWith(".txt") ||
    normalizedName.endsWith(".csv") ||
    normalizedName.endsWith(".json") ||
    normalizedName.endsWith(".xml") ||
    normalizedName.endsWith(".md") ||
    normalizedName.endsWith(".log")
  ) {
    return "text";
  }

  return "unsupported";
}

function buildQuarterRows(schedules) {
  const grouped = new Map();

  (schedules || []).forEach((schedule) => {
    const quarter = schedule.quarter || "-";
    const year = schedule.year || "-";
    const key = `${year}-${quarter}`;

    if (!grouped.has(key)) {
      grouped.set(key, {
        key,
        year,
        quarter,
        months: [],
        statuses: []
      });
    }

    const current = grouped.get(key);

    if (schedule.month) {
      current.months.push(schedule.month);
    }
    if (schedule.status) {
      current.statuses.push(schedule.status);
    }
  });

  return Array.from(grouped.values()).map((item) => {
    const uniqueMonths = Array.from(new Set(item.months));
    const uniqueStatuses = Array.from(new Set(item.statuses));

    let status = "-";
    if (uniqueStatuses.length === 1) {
      status = uniqueStatuses[0];
    } else if (uniqueStatuses.length > 1) {
      status = "Mixed";
    }

    return {
      ...item,
      monthsLabel: uniqueMonths.length ? uniqueMonths.join(", ") : "-",
      status
    };
  });
}

function buildQuarterRowsWithUpdates(schedules, updates) {
  const rows = buildQuarterRows(schedules);

  return rows.map((row) => {
    const rowUpdates = (updates || []).filter(
      (update) => Number(update.year) === Number(row.year) && update.quarter === row.quarter
    );
    const latestUpdate = rowUpdates[0] || null;

    return {
      ...row,
      updates: rowUpdates,
      latestUpdate,
      currentStatus: latestUpdate?.status || row.status,
      latestComment: latestUpdate?.content || "",
      latestUpdateAuthor: latestUpdate ? formatUserName(latestUpdate) : "",
      latestUpdateDate: latestUpdate?.created_at || ""
    };
  });
}

function buildQuarterRowsWithMetadata(schedules, updates, reports) {
  const rows = buildQuarterRowsWithUpdates(schedules, updates);

  return rows.map((row) => {
    const rowReports = (reports || []).filter(
      (report) => Number(report.year) === Number(row.year) && report.quarter === row.quarter
    );

    return {
      ...row,
      reports: rowReports,
      latestReport: rowReports[0] || null
    };
  });
}

const statusOptions = ["Pending", "In Progress", "Completed", "On Hold", "Not Done"];

function ActivityDetailPage() {
  const history = useHistory();
  const { uuid } = useParams();
  const { user } = useAuth();
  const [activity, setActivity] = useState(null);
  const [lookups, setLookups] = useState({
    grants: [],
    implementors: [],
    pillars: []
  });
  const [quarterUpdates, setQuarterUpdates] = useState([]);
  const [quarterReports, setQuarterReports] = useState([]);
  const [activeQuarterRow, setActiveQuarterRow] = useState(null);
  const [activeQuarterModalMode, setActiveQuarterModalMode] = useState("comment");
  const [editingQuarterUpdate, setEditingQuarterUpdate] = useState(null);
  const [quarterUpdateForm, setQuarterUpdateForm] = useState({
    status: "Pending",
    content: "",
    saving: false
  });
  const [activeReportRow, setActiveReportRow] = useState(null);
  const [reportUploadForm, setReportUploadForm] = useState({
    files: [],
    saving: false
  });
  const [previewState, setPreviewState] = useState({
    open: false,
    loading: false,
    file: null,
    previewUrl: "",
    previewText: "",
    previewKind: ""
  });
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  const [financialForm, setFinancialForm] = useState({
    costugx: "",
    costusd: "",
    rate: "",
    committed_funds: "",
    expended_funds: "",
    fully_accounted_funds: "",
    gavi_grant_balances: "",
    saving: false
  });
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [expandedQuarterKeys, setExpandedQuarterKeys] = useState([]);

  useEffect(() => {
    fetchPageData();
  }, [uuid]);

  useEffect(() => {
    return () => {
      if (previewState.previewUrl) {
        window.URL.revokeObjectURL(previewState.previewUrl);
      }
    };
  }, [previewState.previewUrl]);

  const pillarName = useMemo(
    () => lookups.pillars.find((item) => String(item.id) === String(activity?.pillar_id || ""))?.name || "-",
    [activity, lookups.pillars]
  );
  const implementorName = useMemo(
    () => lookups.implementors.find((item) => String(item.id) === String(activity?.implementor_id || ""))?.name || "-",
    [activity, lookups.implementors]
  );
  const grantName = useMemo(
    () => lookups.grants.find((item) => String(item.id) === String(activity?.grant_id || ""))?.name || "-",
    [activity, lookups.grants]
  );
  const quarterRows = useMemo(
    () => buildQuarterRowsWithMetadata(activity?.schedules, quarterUpdates, quarterReports),
    [activity, quarterUpdates, quarterReports]
  );
  const calculatedCostUGX = useMemo(
    () => parseNumber(financialForm.costusd) * parseNumber(financialForm.rate),
    [financialForm.costusd, financialForm.rate]
  );
  const calculatedGrantBalance = useMemo(
    () => parseNumber(financialForm.costusd) - parseNumber(financialForm.fully_accounted_funds),
    [financialForm.costusd, financialForm.fully_accounted_funds]
  );

  async function fetchPageData() {
    setLoading(true);
    setError("");

    try {
      const [activityResponse, lookupData, updatesResponse, reportsResponse] = await Promise.all([
        api.get(`/activities/${uuid}`),
        loadActivityLookups(),
        api.get(`/activities/${uuid}/quarter-updates`),
        api.get(`/activities/${uuid}/reports`)
      ]);

      setActivity(activityResponse.data);
      setLookups({
        grants: lookupData.grants,
        implementors: lookupData.implementors,
        pillars: lookupData.pillars
      });
      setQuarterUpdates(updatesResponse.data || []);
      setQuarterReports(reportsResponse.data || []);
      setFinancialForm((current) => ({
        ...current,
        costugx: activityResponse.data.costugx ?? "",
        costusd: activityResponse.data.costusd ?? "",
        rate: activityResponse.data.rate ?? "",
        committed_funds: activityResponse.data.committed_funds ?? "",
        expended_funds: activityResponse.data.expended_funds ?? "",
        fully_accounted_funds: activityResponse.data.fully_accounted_funds ?? "",
        gavi_grant_balances: activityResponse.data.gavi_grant_balances ?? "",
        saving: false
      }));
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load activity details.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);

    try {
      await api.delete(`/activities/${uuid}`);
      toast.success("Activity deleted");
      history.push("/activities");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete activity.");
      setDeleting(false);
    }
  }

  function renderDetailItem(label, value) {
    return {
      label,
      value: value || "-"
    };
  }

  function renderDetailRows(items) {
    const rows = [];

    for (let index = 0; index < items.length; index += 2) {
      const left = items[index];
      const right = items[index + 1];

      rows.push(
        <tr key={left.label}>
          <th>{left.label}</th>
          <td>{left.value}</td>
          {right ? (
            <>
              <th>{right.label}</th>
              <td>{right.value}</td>
            </>
          ) : (
            <>
              <th className="staff-task-detail-empty"></th>
              <td className="staff-task-detail-empty"></td>
            </>
          )}
        </tr>
      );
    }

    return rows;
  }

  function openQuarterUpdateModal(row, mode = "comment") {
    setEditingQuarterUpdate(null);
    setActiveQuarterRow(row);
    setActiveQuarterModalMode(mode);
    setQuarterUpdateForm({
      status: row.currentStatus && row.currentStatus !== "Mixed" ? row.currentStatus : "Pending",
      content: "",
      saving: false
    });
  }

  function openQuarterCommentEditModal(row, update) {
    setActiveQuarterRow(row);
    setActiveQuarterModalMode("edit-comment");
    setEditingQuarterUpdate(update);
    setQuarterUpdateForm({
      status: update.status || row.currentStatus || "Pending",
      content: update.content || "",
      saving: false
    });
  }

  function closeQuarterUpdateModal() {
    setActiveQuarterRow(null);
    setActiveQuarterModalMode("comment");
    setEditingQuarterUpdate(null);
    setQuarterUpdateForm({
      status: "Pending",
      content: "",
      saving: false
    });
  }

  function toggleQuarterExpansion(rowKey) {
    setExpandedQuarterKeys((current) =>
      current.includes(rowKey) ? current.filter((item) => item !== rowKey) : [...current, rowKey]
    );
  }

  function openReportUploadModal(row) {
    setActiveReportRow(row);
    setReportUploadForm({
      files: [],
      saving: false
    });
  }

  function closeReportUploadModal() {
    setActiveReportRow(null);
    setReportUploadForm({
      files: [],
      saving: false
    });
  }

  function openFinancialModal() {
    if (!activity) {
      return;
    }

    setFinancialForm({
      costugx: activity.costugx ?? "",
      costusd: activity.costusd ?? "",
      rate: activity.rate ?? "",
      committed_funds: activity.committed_funds ?? "",
      expended_funds: activity.expended_funds ?? "",
      fully_accounted_funds: activity.fully_accounted_funds ?? "",
      gavi_grant_balances: activity.gavi_grant_balances ?? "",
      saving: false
    });
    setShowFinancialModal(true);
  }

  function closeFinancialModal() {
    setShowFinancialModal(false);
    setFinancialForm((current) => ({
      ...current,
      saving: false
    }));
  }

  async function handleQuarterUpdateSave() {
    if (!activeQuarterRow) {
      return;
    }
    if (activeQuarterModalMode !== "status" && !quarterUpdateForm.content.trim()) {
      toast.error("Comment is required.");
      return;
    }

    setQuarterUpdateForm((current) => ({
      ...current,
      saving: true
    }));

    try {
      const payload = {
        year: Number(activeQuarterRow.year),
        quarter: activeQuarterRow.quarter,
        status: quarterUpdateForm.status,
        content: quarterUpdateForm.content.trim() || quarterUpdateForm.status
      };
      if (editingQuarterUpdate) {
        await api.put(`/activities/${uuid}/quarter-updates/${editingQuarterUpdate.uuid}`, payload);
      } else {
        await api.post(`/activities/${uuid}/quarter-updates`, payload);
      }
      toast.success(editingQuarterUpdate ? "Comment updated" : `${activeQuarterRow.quarter} update saved`);
      await fetchPageData();
      closeQuarterUpdateModal();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save quarter update.");
      setQuarterUpdateForm((current) => ({
        ...current,
        saving: false
      }));
    }
  }

  async function handleQuarterReportUpload() {
    if (!activeReportRow) {
      return;
    }
    if (!reportUploadForm.files.length) {
      toast.error("Please choose at least one report file.");
      return;
    }

    setReportUploadForm((current) => ({
      ...current,
      saving: true
    }));

    const formData = new FormData();
    reportUploadForm.files.forEach((file) => {
      formData.append("reports", file);
    });
    formData.append("year", String(activeReportRow.year));
    formData.append("quarter", activeReportRow.quarter);

    try {
      await api.post(`/activities/${uuid}/reports`, formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success(
        reportUploadForm.files.length === 1
          ? `${activeReportRow.quarter} report uploaded`
          : `${reportUploadForm.files.length} ${activeReportRow.quarter} reports uploaded`
      );
      await fetchPageData();
      closeReportUploadModal();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to upload quarter report.");
      setReportUploadForm((current) => ({
        ...current,
        saving: false
      }));
    }
  }

  async function handleReportDelete(report) {
    try {
      await api.delete(`/activities/${uuid}/reports/${report.uuid}`);
      toast.success("Report deleted");
      await fetchPageData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete report.");
    }
  }

  async function handleFinancialSave() {
    if (!activity) {
      return;
    }

    setFinancialForm((current) => ({
      ...current,
      saving: true
    }));

    const payload = {
      code: activity.code,
      name: activity.name,
      level: activity.level,
      objectives: activity.objectives,
      pillar_id: activity.pillar_id || null,
      implementor_id: activity.implementor_id || null,
      grant_id: activity.grant_id || null,
      costugx: calculatedCostUGX,
      costusd: numberOrNull(financialForm.costusd),
      rate: numberOrNull(financialForm.rate),
      committed_funds: numberOrNull(financialForm.committed_funds),
      expended_funds: numberOrNull(financialForm.expended_funds),
      fully_accounted_funds: numberOrNull(financialForm.fully_accounted_funds),
      gavi_grant_balances: calculatedGrantBalance,
      schedules: (activity.schedules || []).map((schedule) => ({
        month_date: schedule.month_date,
        status: schedule.status,
        year: schedule.year
      }))
    };

    try {
      await api.put(`/activities/${uuid}`, payload);
      toast.success("Financial details updated");
      await fetchPageData();
      closeFinancialModal();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update financial details.");
      setFinancialForm((current) => ({
        ...current,
        saving: false
      }));
    }
  }

  function handleFinancialInputChange(field, value) {
    setFinancialForm((current) => ({
      ...current,
      [field]: normalizeNumberInput(value)
    }));
  }

  async function handleReportDownload(report) {
    try {
      const response = await api.get(`/activities/${uuid}/reports/${report.uuid}`, {
        responseType: "blob"
      });
      downloadBlob(response.data, report.file_name);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to download report.");
    }
  }

  async function handleReportPreview(report) {
    setPreviewState((current) => {
      if (current.previewUrl) {
        window.URL.revokeObjectURL(current.previewUrl);
      }

      return {
        open: true,
        loading: true,
        file: report,
        previewUrl: "",
        previewText: "",
        previewKind: inferPreviewKind(report.file_name, report.file_type)
      };
    });

    try {
      const response = await api.get(`/activities/${uuid}/reports/${report.uuid}`, {
        responseType: "blob"
      });

      const resolvedKind = inferPreviewKind(report.file_name, response.data?.type || report.file_type);
      const previewUrl = resolvedKind === "unsupported" ? "" : window.URL.createObjectURL(response.data);
      const previewText = resolvedKind === "text" ? await response.data.text() : "";

      setPreviewState({
        open: true,
        loading: false,
        file: report,
        previewUrl,
        previewText,
        previewKind: resolvedKind
      });
    } catch (err) {
      setPreviewState({
        open: false,
        loading: false,
        file: null,
        previewUrl: "",
        previewText: "",
        previewKind: ""
      });
      toast.error(err.response?.data?.error || "Failed to preview report.");
    }
  }

  function closePreviewModal() {
    setPreviewState((current) => {
      if (current.previewUrl) {
        window.URL.revokeObjectURL(current.previewUrl);
      }

      return {
        open: false,
        loading: false,
        file: null,
        previewUrl: "",
        previewText: "",
        previewKind: ""
      };
    });
  }

  return (
    <div>
      <div className="activity-detail-header">
        <PageHeader
          title="Activity Details"
          subtitle={
            activity ? (
              <div className="activity-detail-hero">
                <div className="activity-detail-title">{activity.name}</div>
              </div>
            ) : (
              "Loading activity details..."
            )
          }
          action={
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" onClick={() => history.push("/activities")}>
                Back to activities
              </Button>
              <Button variant="primary" onClick={() => history.push(`/activities/${uuid}/edit`)}>
                Edit activity
              </Button>
              <Button variant="outline-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete activity"}
              </Button>
            </div>
          }
        />
      </div>

      {loading ? <LoadingState label="Loading activity details..." /> : null}
      {error ? <Alert variant="danger">{error}</Alert> : null}

      {!loading && activity ? (
        <div className="d-flex flex-column gap-4">
          <div className="content-card activity-detail-section">
            <h5 className="mb-3">Schedule plan</h5>
            {quarterRows.length ? (
              <div className="activity-quarter-latest-grid">
                {quarterRows.map((scheduleRow) => (
                  <div className="activity-quarter-latest-card" key={`${scheduleRow.key}-plan`}>
                    <div className="activity-quarter-latest-head">
                      <span className="detail-item-label mb-0">Scheduled quarter</span>
                      <span className="activity-quarter-table-status">{scheduleRow.quarter}</span>
                    </div>
                    <div className="activity-quarter-latest-months">Year: {scheduleRow.year}</div>
                    <div className="activity-quarter-latest-comment">{scheduleRow.monthsLabel}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mb-0 text-muted">No scheduled months have been added for this activity.</p>
            )}
          </div>

          <div className="content-card activity-detail-section">
            <h5 className="mb-3">Core details</h5>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0 staff-task-detail-table">
                <tbody>
                  {renderDetailRows([
                    renderDetailItem("Code", activity.code),
                    renderDetailItem("Level", activity.level || "-"),
                    renderDetailItem("Pillar", pillarName),
                    renderDetailItem("Implementor", implementorName),
                    renderDetailItem("Grant", grantName),
                    renderDetailItem("Created", formatDate(activity.created_at)),
                    renderDetailItem("Last updated", formatDate(activity.last_updated))
                  ])}
                </tbody>
              </table>
            </div>
          </div>

          <div className="content-card activity-detail-section">
            <h5 className="mb-3">Objectives</h5>
            <p className="mb-0 text-muted">{activity.objectives || "No objectives provided."}</p>
          </div>

          <div className="content-card activity-detail-section">
            <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
              <h5 className="mb-0">Financial details</h5>
              <Button variant="outline-primary" size="sm" onClick={openFinancialModal}>
                Update financial details
              </Button>
            </div>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0 staff-task-detail-table">
                <tbody>
                  {renderDetailRows([
                    renderDetailItem("Cost UGX", formatCurrency(activity.costugx)),
                    renderDetailItem("Cost USD", formatCurrency(activity.costusd)),
                    renderDetailItem("Rate", formatCurrency(activity.rate)),
                    renderDetailItem("Committed funds", formatCurrency(activity.committed_funds)),
                    renderDetailItem("Expended funds", formatCurrency(activity.expended_funds)),
                    renderDetailItem("Fully accounted funds", formatCurrency(activity.fully_accounted_funds)),
                    renderDetailItem("Grant Balance", formatCurrency(activity.gavi_grant_balances))
                  ])}
                </tbody>
              </table>
            </div>
          </div>

          <div className="content-card table-card activity-detail-section">
            <h5 className="mb-3">Quarter status updates</h5>
            {quarterRows.length ? (
              <div className="d-flex flex-column gap-3">
                {quarterRows.map((scheduleRow) => {
                  const isExpanded = expandedQuarterKeys.includes(scheduleRow.key);

                  return (
                    <div key={scheduleRow.key} className="border rounded-4 p-3">
                      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                        <div className="d-flex flex-column gap-2">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="fw-semibold">{scheduleRow.quarter}</span>
                            <span className="text-muted">{scheduleRow.year}</span>
                            <span className={getActivityStatusBadgeClass(scheduleRow.currentStatus)}>{scheduleRow.currentStatus || "-"}</span>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap justify-content-end gap-2">
                          <Button size="sm" onClick={() => openQuarterUpdateModal(scheduleRow, "status")}>
                            Update status
                          </Button>
                          <Button variant="outline-primary" size="sm" onClick={() => openQuarterUpdateModal(scheduleRow, "comment")}>
                            Add comment
                          </Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => openReportUploadModal(scheduleRow)}>
                            Upload report
                          </Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => toggleQuarterExpansion(scheduleRow.key)}>
                            {isExpanded ? "Hide comments" : "View comments"}
                          </Button>
                        </div>
                      </div>

                      {isExpanded ? (
                        <div className="mt-4 d-flex flex-column gap-4">
                          <div className="border rounded-4 p-3">
                            <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                              <div className="small text-uppercase text-muted">Status Update Comments</div>
                              <div className="small text-muted">{scheduleRow.updates.length} {scheduleRow.updates.length === 1 ? "entry" : "entries"}</div>
                            </div>
                            {scheduleRow.updates.length ? (
                              <div className="d-flex flex-column gap-3">
                                {scheduleRow.updates.map((update) => (
                                  <div key={update.uuid} className="border-start border-3 ps-3">
                                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-1">
                                      <div className="d-flex flex-wrap align-items-center gap-2">
                                        <span className="fw-semibold">{formatUserName(update)}</span>
                                        <span className="text-muted small">{formatDateTime(update.last_updated || update.created_at)}</span>
                                      </div>
                                      {user?.uuid === update.user_uuid ? (
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          onClick={() => openQuarterCommentEditModal(scheduleRow, update)}
                                        >
                                          Edit
                                        </Button>
                                      ) : null}
                                    </div>
                                    <div className="small">{update.content || "-"}</div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="small text-muted">No comments have been added for this quarter yet.</div>
                            )}
                          </div>

                          <div className="border rounded-4 p-3">
                            <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                              <div className="small text-uppercase text-muted">Documents</div>
                              <div className="small text-muted">{scheduleRow.reports.length} file{scheduleRow.reports.length === 1 ? "" : "s"}</div>
                            </div>
                            {scheduleRow.reports.length ? (
                              <div className="d-flex flex-column gap-2">
                                {scheduleRow.reports.map((report) => (
                                  <div key={report.uuid} className="activity-quarter-report-item">
                                    <div className="activity-quarter-report-main">
                                      <div className="activity-quarter-report-icon" aria-hidden="true">
                                        <i className="bi bi-file-earmark-text"></i>
                                      </div>
                                      <div className="activity-quarter-report-copy">
                                      <button
                                        type="button"
                                        className="activity-quarter-report-link"
                                        onClick={() => handleReportPreview(report)}
                                      >
                                        {report.file_name}
                                      </button>
                                      <div className="activity-quarter-report-meta">{formatDateTime(report.created_at)}</div>
                                    </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleReportDownload(report)}
                                        title="Download report"
                                      >
                                        <i className="bi bi-download"></i>
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleReportDelete(report)}
                                        title="Delete report"
                                      >
                                        <i className="bi bi-trash"></i>
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="small text-muted">No report uploaded yet.</div>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="mb-0 text-muted">No quarter rows are available for this activity yet.</p>
            )}
          </div>
        </div>
      ) : null}

      <Modal show={Boolean(activeQuarterRow)} onHide={closeQuarterUpdateModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {activeQuarterRow
              ? `${activeQuarterRow.quarter} ${activeQuarterRow.year} ${activeQuarterModalMode === "status" ? "status update" : editingQuarterUpdate ? "edit comment" : "status update comment"}`
              : "Status update"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeQuarterRow ? (
            <div className="d-flex flex-column gap-3">
              <div className="small text-muted">Months: {activeQuarterRow.monthsLabel}</div>
              {activeQuarterModalMode === "status" ? (
                <div>
                  <label className="form-label">Status</label>
                  <Form.Select
                    value={quarterUpdateForm.status}
                    onChange={(event) =>
                      setQuarterUpdateForm((current) => ({
                        ...current,
                        status: event.target.value
                      }))
                    }
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              ) : null}
              {activeQuarterModalMode !== "status" ? (
                <div>
                  <label className="form-label">Comment</label>
                  <Form.Control
                    as="textarea"
                    rows={10}
                    value={quarterUpdateForm.content}
                    onChange={(event) =>
                      setQuarterUpdateForm((current) => ({
                        ...current,
                        content: event.target.value
                      }))
                    }
                    placeholder={`Enter ${activeQuarterRow.quarter} comment`}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeQuarterUpdateModal}>
            Cancel
          </Button>
          <Button onClick={handleQuarterUpdateSave} disabled={quarterUpdateForm.saving}>
            {quarterUpdateForm.saving ? "Saving..." : activeQuarterModalMode === "status" ? "Save status" : editingQuarterUpdate ? "Save changes" : "Save comment"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(activeReportRow)} onHide={closeReportUploadModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {activeReportRow ? `${activeReportRow.quarter} ${activeReportRow.year} report upload` : "Report upload"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeReportRow ? (
            <div className="d-flex flex-column gap-3">
              <div className="small text-muted">Months: {activeReportRow.monthsLabel}</div>
              <div>
                <label className="form-label">Report files</label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={(event) =>
                    setReportUploadForm((current) => ({
                      ...current,
                      files: Array.from(event.target.files || [])
                    }))
                  }
                />
                {activeReportRow.reports.length ? (
                  <div className="small text-muted mt-2">
                    Existing files for this quarter: {activeReportRow.reports.length}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeReportUploadModal}>
            Cancel
          </Button>
          <Button onClick={handleQuarterReportUpload} disabled={reportUploadForm.saving}>
            {reportUploadForm.saving ? "Uploading..." : "Upload report"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={previewState.open} onHide={closePreviewModal} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{previewState.file?.file_name || "Report preview"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewState.loading ? <LoadingState label="Loading preview..." /> : null}

          {!previewState.loading && previewState.previewKind === "image" ? (
            <div className="text-center">
              <img
                src={previewState.previewUrl}
                alt={previewState.file?.file_name || "Report preview"}
                style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
              />
            </div>
          ) : null}

          {!previewState.loading && previewState.previewKind === "pdf" ? (
            <iframe
              title={previewState.file?.file_name || "Report preview"}
              src={previewState.previewUrl}
              style={{ width: "100%", height: "70vh", border: 0 }}
            />
          ) : null}

          {!previewState.loading && previewState.previewKind === "video" ? (
            <video src={previewState.previewUrl} controls style={{ width: "100%", maxHeight: "70vh" }} />
          ) : null}

          {!previewState.loading && previewState.previewKind === "audio" ? (
            <audio src={previewState.previewUrl} controls style={{ width: "100%" }} />
          ) : null}

          {!previewState.loading && previewState.previewKind === "text" ? (
            <pre
              style={{
                maxHeight: "70vh",
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                marginBottom: 0
              }}
            >
              {previewState.previewText || "This file is empty."}
            </pre>
          ) : null}

          {!previewState.loading && previewState.previewKind === "unsupported" ? (
            <Alert variant="light" className="mb-0 document-preview-unsupported-alert">
              This file type cannot be previewed directly in the browser. Use the download button to open it in the appropriate application.
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closePreviewModal}>
            Close
          </Button>
          <Button
            onClick={() => (previewState.file ? handleReportDownload(previewState.file) : null)}
            disabled={!previewState.file}
          >
            Download
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showFinancialModal} onHide={closeFinancialModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update financial details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <label className="form-label">Cost UGX equivalent</label>
              <Form.Control
                value={formatNumberInput(calculatedCostUGX)}
                readOnly
                disabled
                className="calculated-input"
              />
            </Col>
            <Col md={6}>
              <label className="form-label">Cost USD</label>
              <Form.Control
                value={formatNumberInput(financialForm.costusd)}
                onChange={(event) => handleFinancialInputChange("costusd", event.target.value)}
              />
            </Col>
            <Col md={6}>
              <label className="form-label">Rate</label>
              <Form.Control
                value={formatNumberInput(financialForm.rate)}
                onChange={(event) => handleFinancialInputChange("rate", event.target.value)}
              />
            </Col>
            <Col md={6}>
              <label className="form-label">Committed funds</label>
              <Form.Control
                value={formatNumberInput(financialForm.committed_funds)}
                onChange={(event) => handleFinancialInputChange("committed_funds", event.target.value)}
              />
            </Col>
            <Col md={6}>
              <label className="form-label">Expended funds</label>
              <Form.Control
                value={formatNumberInput(financialForm.expended_funds)}
                onChange={(event) => handleFinancialInputChange("expended_funds", event.target.value)}
              />
            </Col>
            <Col md={6}>
              <label className="form-label">Fully accounted funds</label>
              <Form.Control
                value={formatNumberInput(financialForm.fully_accounted_funds)}
                onChange={(event) => handleFinancialInputChange("fully_accounted_funds", event.target.value)}
              />
            </Col>
            <Col xs={12}>
              <label className="form-label">Grant Balance</label>
              <Form.Control
                value={formatNumberInput(calculatedGrantBalance)}
                readOnly
                disabled
                className="calculated-input"
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeFinancialModal}>
            Cancel
          </Button>
          <Button onClick={handleFinancialSave} disabled={financialForm.saving}>
            {financialForm.saving ? "Saving..." : "Save financial details"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ActivityDetailPage;
